/**
 * The companion's voice agent on Cloudflare (voice-stacks/hooperbuddy-voice-cf), from the page.
 *
 * Copied to public/js/voicecf.js by the handoff; this file is the source. No library, nothing from
 * another host, no eval and no blob: worklet, so the app's content security policy allows all of it.
 *
 * One WebSocket to the node carries everything that is not audio (protocol v1, see
 * container/hbcf/protocol.py). The audio goes one of two ways:
 *   ws   the microphone as 16 kHz 16 bit PCM on the same socket, her voice back as 24 kHz PCM
 *        (path A). Played through Web Audio on a clock this file keeps, so a barge-in stops her at
 *        once and every marker ("that line finished playing") fires when the sound actually ends.
 *   sfu  WebRTC to Cloudflare's Realtime SFU (path B): the microphone is published there, the SFU
 *        streams it to the node, and her voice comes back as a WebRTC track. The socket carries the
 *        signalling. Echo cancellation treats a WebRTC track as the far end on every browser.
 *
 * The page stays in charge of what happens: every finished thing the player says comes here first
 * (onTurn), and the page answers with what to do, exactly as its own talk loop decides for the
 * browser lane. Everything the brain decides (navigate, mood, a booking, a logged meal, the card to
 * confirm, ending the session) comes back through onMeta for the page's own handlers.
 *
 *   const agent = await connectAgent({ url, token, voice, onTurn, onMeta, ... });
 *   agent.say('Nice shot.', { kind: 'reaction' })   // her voice, in order, interruptible
 *   agent.text('what do you see here?', hooper)     // a turn for the brain without the microphone
 *   agent.context({ training: true, view: 'train' })
 *   agent.hush(); agent.stop();
 */

const CONNECT_MS = 10000;         // from the tap to her ready, a cold container included (5.5 to 7.5 s measured on
                                  // staging, 2026-09-25); slower and the browser lane takes over
const MIC = { echoCancellation: true, noiseSuppression: true, autoGainControl: true };
const IN_RATE = 16000;            // what the pipeline hears
const OUT_RATE = 24000;           // what she speaks on path A
const FRAME = 320;                // 20 ms of microphone a message
const LEAD_S = 0.06;              // scheduling margin for her first chunk
const TAP_WORKLET = '/worklets/hb-tap.js';
const ICE = [{ urls: 'stun:stun.cloudflare.com:3478' }];
const SFU_LAG_S = 0.25;           // path B: how far her WebRTC audio trails the control socket, for markers

const isChromiumDesktop = () => /Chrome\//.test(navigator.userAgent) && !/Mobile|Android/.test(navigator.userAgent);

/**
 * @param url        the node's origin (https://...) or ws(s) base; the call is `${url}/call`
 * @param token      the signed session token from POST /api/voice/session
 * @param transport  'ws' (default) or 'sfu'
 * @param voice      the companion's Aura speaker ("aura-arcas")
 * @param stt        'flux' | 'whisper' | '' (the node's default)
 * @param onTurn     (text, id) => decision | Promise<decision>: { go: 'brain'|'skip', text?, hooper?, ack? }
 * @param onMeta     (meta, id) => void: navigate, mood, event, eventFailed, action, endSession
 * @param onSaid     (text, id) => void: her whole reply, every word out
 * @param onUserText (text) => void: a finished thing the player said
 * @param onPartial  (text) => void: words as they are heard
 * @param onBotText  (text, done) => void: her reply as it streams; done carries the whole reply
 * @param onState    (state) => void: starting (a container is coming up) | listening | thinking | talking
 * @param onEnd      ({ reason, message }) => void: the call ended for any reason but stop()
 * @param onEvent    (message) => void: every protocol message, for diagnostics
 * @param stream     optional MediaStream to send instead of the microphone (tests)
 * @param aecLoopback play her voice through a local WebRTC loopback so Chrome's echo canceller sees
 *                   it (path A). Default on for desktop Chromium, off elsewhere.
 * @returns the agent, once she is ready
 */
export async function connectAgent({
  url, token, transport = 'ws', voice = '', stt = '', hello = false, timeoutMs = CONNECT_MS,
  onTurn, onMeta, onSaid, onUserText, onPartial, onBotText, onState, onEnd, onEvent,
  stream: given = null, aecLoopback = isChromiumDesktop(),
} = {}) {
  const base = String(url).replace(/\/$/, '').replace(/^http/, 'ws');
  const q = new URLSearchParams({ transport: transport === 'sfu' ? 'sfu' : 'ws', voice: String(voice || ''), hello: hello ? '1' : '0' });
  if (stt) q.set('stt', stt);

  let ws = null, ctx = null, mic = null, tap = null, pc = null, herEl = null, loop = null;
  let ready = false, ended = false, timer = null;
  let replying = false, reply = '';
  const pending = new Map();        // say id -> { resolve, kind }
  let player = null;
  const state = (s) => { try { onState?.(s); } catch {} };

  /* ------------------------------------------------------------ her voice, path A */
  function createPlayer(audioCtx, destination) {
    let next = 0;                    // when the last scheduled chunk ends, in audioCtx time
    const live = new Set();
    const marks = [];                // { at, fire } waiting for the audio before them to finish
    let tick = null;
    const pump = () => {
      clearTimeout(tick); tick = null;
      const t = audioCtx.currentTime;
      while (marks.length && marks[0].at <= t + 0.005) { const m = marks.shift(); try { m.fire(); } catch {} }
      if (marks.length) tick = setTimeout(pump, Math.max(5, (marks[0].at - t) * 1000));
    };
    return {
      play(int16) {
        const n = int16.length; if (!n) return;
        const buf = audioCtx.createBuffer(1, n, OUT_RATE);
        const ch = buf.getChannelData(0);
        for (let i = 0; i < n; i++) ch[i] = int16[i] / 32768;
        const src = audioCtx.createBufferSource();
        src.buffer = buf; src.connect(destination);
        const t = audioCtx.currentTime;
        if (next < t + LEAD_S) next = t + LEAD_S;
        src.start(next); next += buf.duration;
        live.add(src); src.onended = () => live.delete(src);
      },
      /** Fire once everything queued before now has played. */
      mark(fire) { marks.push({ at: Math.max(next, audioCtx.currentTime), fire }); if (!tick) pump(); },
      /** Stop her now: drop what is queued, fire the waiting marks. */
      flush() {
        for (const s of live) { try { s.stop(); } catch {} }
        live.clear(); next = 0;
        const waiting = marks.splice(0); for (const m of waiting) { try { m.fire(true); } catch {} }
      },
      get busy() { return next > audioCtx.currentTime + 0.01; },
      close() { clearTimeout(tick); this.flush(); },
    };
  }

  /** Path B has no clock of its own for her voice: markers fire a little after they arrive. */
  function createSfuPlayer() {
    let talking = false; let until = 0;
    return {
      play() {},
      mark(fire) { setTimeout(() => fire(), SFU_LAG_S * 1000); },
      flush() {},
      set talking(v) { talking = v; if (!v) until = performance.now() + SFU_LAG_S * 1000; },
      get busy() { return talking || performance.now() < until; },
      close() {},
    };
  }

  /**
   * Chrome cancels echo only for audio that arrives over WebRTC. Her voice (Web Audio) is sent
   * through a local RTCPeerConnection pair and played from the far end, so the canceller has it.
   */
  async function loopbackOut(audioCtx) {
    const dest = audioCtx.createMediaStreamDestination();
    const a = new RTCPeerConnection(); const b = new RTCPeerConnection();
    a.onicecandidate = (e) => { if (e.candidate) b.addIceCandidate(e.candidate).catch(() => {}); };
    b.onicecandidate = (e) => { if (e.candidate) a.addIceCandidate(e.candidate).catch(() => {}); };
    const got = new Promise((resolve) => { b.ontrack = (e) => resolve(e.streams[0] || new MediaStream([e.track])); });
    dest.stream.getTracks().forEach((t) => a.addTrack(t, dest.stream));
    await a.setLocalDescription(await a.createOffer());
    await b.setRemoteDescription(a.localDescription);
    await b.setLocalDescription(await b.createAnswer());
    await a.setRemoteDescription(b.localDescription);
    const remote = await Promise.race([got, new Promise((_, rej) => setTimeout(() => rej(new Error('loopback')), 2000))]);
    const el = Object.assign(document.createElement('audio'), { autoplay: true });
    el.setAttribute('playsinline', ''); el.style.display = 'none'; el.srcObject = remote;
    document.body.append(el); el.play().catch(() => {});
    return { node: dest, close() { try { a.close(); b.close(); el.srcObject = null; el.remove(); } catch {} } };
  }

  /* ------------------------------------------------------------ the microphone, path A */
  function startMic(audioCtx, source) {
    const step = audioCtx.sampleRate / IN_RATE;
    const frame = new Int16Array(FRAME); let fill = 0, acc = 0, n = 0, pos = 0;
    tap.port.onmessage = (e) => {
      const f32 = e.data;
      for (let k = 0; k < f32.length; k++) {
        acc += f32[k]; n++; pos += 1;
        if (pos < step) continue;
        pos -= step;
        const v = Math.max(-1, Math.min(1, acc / n)); acc = 0; n = 0;
        frame[fill++] = v < 0 ? v * 0x8000 : v * 0x7fff;
        if (fill === FRAME) { if (ws?.readyState === 1) ws.send(frame.slice(0).buffer); fill = 0; }
      }
    };
    source.connect(tap);
  }

  /* ------------------------------------------------------------ path B signalling */
  const gathered = (peer, ms) => new Promise((resolve) => {
    if (peer.iceGatheringState === 'complete') return resolve();
    const done = () => { clearTimeout(t); peer.removeEventListener('icegatheringstatechange', check); resolve(); };
    const check = () => { if (peer.iceGatheringState === 'complete') done(); };
    const t = setTimeout(done, ms);
    peer.addEventListener('icegatheringstatechange', check);
  });
  async function sfuOffer() {
    pc = new RTCPeerConnection({ iceServers: ICE, bundlePolicy: 'max-bundle' });
    const [track] = mic.getAudioTracks();
    const tx = pc.addTransceiver(track, { direction: 'sendonly' });
    pc.ontrack = (e) => {
      if (e.track.kind !== 'audio') return;
      herEl ??= Object.assign(document.createElement('audio'), { autoplay: true });
      herEl.setAttribute('playsinline', ''); herEl.style.display = 'none';
      if (!herEl.isConnected) document.body.append(herEl);
      herEl.srcObject = e.streams[0] || new MediaStream([e.track]);
      herEl.play().catch(() => {});
    };
    pc.onconnectionstatechange = () => {
      if (pc?.connectionState === 'connected') send({ hb: 1, t: 'sfu-connected' });
      if (['failed', 'closed'].includes(pc?.connectionState)) drop('The call lost its audio.');
    };
    await pc.setLocalDescription(await pc.createOffer());
    await gathered(pc, 1200);
    send({ hb: 1, t: 'sfu-offer', sdp: pc.localDescription.sdp, mid: tx.mid });
  }
  async function onSfu(d) {
    if (d.t === 'sfu-answer') await pc.setRemoteDescription({ type: 'answer', sdp: d.sdp });
    else if (d.t === 'sfu-offer') {
      await pc.setRemoteDescription({ type: 'offer', sdp: d.sdp });
      await pc.setLocalDescription(await pc.createAnswer());
      await gathered(pc, 1200);
      send({ hb: 1, t: 'sfu-answer', sdp: pc.localDescription.sdp });
    }
  }

  /* ------------------------------------------------------------ protocol */
  const send = (msg) => { if (ws?.readyState === 1) ws.send(JSON.stringify(msg)); };
  const uid = () => (crypto.randomUUID ? crypto.randomUUID() : String(Math.random()).slice(2)).replace(/-/g, '').slice(0, 12);
  const endReply = () => {
    if (!replying) return;
    replying = false; const text = reply.trim(); reply = '';
    try { onBotText?.(text, true); } catch {}
  };

  let settle;
  const started = new Promise((resolve, reject) => { settle = { resolve, reject }; });
  const teardown = () => {
    clearTimeout(timer);
    try { ws?.close(1000, 'bye'); } catch {}
    try { pc?.close(); } catch {}
    try { player?.close(); } catch {}
    try { loop?.close(); } catch {}
    if (mic && mic !== given) mic.getTracks().forEach((t) => t.stop());
    try { tap?.port && (tap.port.onmessage = null); } catch {}
    try { ctx?.close(); } catch {}
    if (herEl) { try { herEl.srcObject = null; herEl.remove(); } catch {} }
    for (const [, p] of pending) { try { p.resolve({ interrupted: true, ended: true }); } catch {} }
    pending.clear();
    ws = pc = player = loop = mic = tap = ctx = herEl = null;
  };
  const fail = (e) => { if (ready || ended) return; ended = true; teardown(); settle.reject(e instanceof Error ? e : new Error(String(e))); };
  const drop = (message = '', reason = 'dropped') => {
    if (!ready) return fail(Object.assign(new Error(message || 'The voice agent hung up before the call started.'), { reason }));
    if (ended) return;
    ended = true; endReply(); teardown(); state('off');
    try { onEnd?.({ reason, message }); } catch {}
  };

  async function onMessage(ev) {
    if (typeof ev.data !== 'string') {
      // Her voice (path A): 16 bit mono PCM at 24 kHz.
      const buf = ev.data instanceof ArrayBuffer ? ev.data : await ev.data.arrayBuffer();
      if (buf.byteLength >= 2) player?.play(new Int16Array(buf, 0, buf.byteLength >> 1));
      return;
    }
    let m; try { m = JSON.parse(ev.data); } catch { return; }
    try { onEvent?.(m); } catch {}
    const d = m.data || {};
    if (m.type === 'server-message' && d.hb) return onServer(d);
    switch (m.type) {
      case 'bot-ready':
        if (!ready && !ended) { ready = true; clearTimeout(timer); state('listening'); settle.resolve(agent); }
        break;
      case 'user-started-speaking': state('listening'); break;
      case 'user-stopped-speaking': state('thinking'); break;
      case 'user-transcription': {
        const text = String(d.text || '').trim(); if (!text) break;
        if (d.final) { try { onUserText?.(text); } catch {} } else { try { onPartial?.(text); } catch {} }
        break;
      }
      case 'bot-llm-started': endReply(); replying = true; reply = ''; break;
      case 'bot-llm-text': { const t = String(d.text || ''); if (!t) break; replying = true; reply += t; try { onBotText?.(t, false); } catch {} break; }
      case 'bot-llm-stopped': endReply(); break;
      case 'bot-started-speaking': if (transport === 'sfu' && player) player.talking = true; state('talking'); break;
      case 'bot-stopped-speaking': if (transport === 'sfu' && player) player.talking = false; state('listening'); break;
      default: break;
    }
  }

  async function onServer(d) {
    switch (d.t) {
      case 'turn': {
        // The page's gate: the same decision its own talk loop makes for the browser lane.
        let decision = { go: 'brain' };
        try { decision = (await onTurn?.(d.text, d.id)) || { go: 'brain' }; } catch (e) { console.warn('turn gate failed', e); }
        send({ hb: 1, t: 'turn', id: d.id, ...decision });
        break;
      }
      case 'meta': { const { hb, t, id, ...meta } = d; try { onMeta?.(meta, id); } catch (e) { console.warn('meta handler', e); } break; }
      case 'said': try { onSaid?.(d.text, d.id); } catch {} break;
      case 'say-done': player?.mark((cut) => { const p = pending.get(d.id); if (p) { pending.delete(d.id); p.resolve({ interrupted: Boolean(cut), dropped: Boolean(d.dropped) }); } }); break;
      case 'interrupted': {
        player?.flush();
        for (const id of d.says || []) { const p = pending.get(id); if (p) { pending.delete(id); p.resolve({ interrupted: true }); } }
        endReply();
        break;
      }
      case 'clear': player?.flush(); break;
      case 'end': {
        const reason = d.reason || 'ended';
        if (!ready) return fail(Object.assign(new Error(d.message || 'The voice agent could not start.'), { reason }));
        return drop(d.message || '', reason);
      }
      case 'sfu-answer': case 'sfu-offer': onSfu(d).catch((e) => fail(e)); break;
      case 'status': if (d.state === 'starting' && !ready) state('starting'); break;
      default: break;
    }
  }

  const agent = {
    transport,
    /** Speak this exact line in her voice, after anything already queued. Resolves when it has played. */
    say(text, { kind = 'line', interrupt = false, replace = false } = {}) {
      const id = uid();
      return new Promise((resolve) => {
        if (ended || !String(text || '').trim()) return resolve({ interrupted: false, empty: true });
        pending.set(id, { resolve, kind });
        send({ hb: 1, t: 'say', id, text: String(text), kind, interrupt, replace });
      });
    },
    /** A typed or page-made turn for the brain (no gate: the page already decided). */
    text(text, hooper) { const id = uid(); send({ hb: 1, t: 'text', id, text: String(text || ''), ...(hooper ? { hooper } : {}) }); return id; },
    /** What the page knows: training, view, seen, film, lastShot, voice, keepAlive, acks. */
    context(obj) { send({ hb: 1, t: 'context', ...obj }); },
    /** Stop talking now. */
    hush() { player?.flush(); send({ hb: 1, t: 'hush' }); },
    /** True while any of her voice is playing or about to. */
    get speaking() { return Boolean(player?.busy) || replying || pending.size > 0; },
    get live() { return ready && !ended; },
    stop() { if (ended) return; ended = true; send({ hb: 1, t: 'bye' }); setTimeout(teardown, 150); state('off'); },
  };

  try {
    // Asked for first, so a permission prompt is not counted against the connection's time.
    mic = given || await navigator.mediaDevices.getUserMedia({ audio: MIC });
    timer = setTimeout(() => fail(new Error(`The voice agent did not answer within ${timeoutMs / 1000} s.`)), timeoutMs);
    if (transport !== 'sfu') {
      const AC = window.AudioContext || window.webkitAudioContext;
      ctx = new AC({ latencyHint: 'interactive' });
      if (ctx.state === 'suspended') { try { await ctx.resume(); } catch {} }
      await ctx.audioWorklet.addModule(TAP_WORKLET);
      tap = new AudioWorkletNode(ctx, 'hb-tap', { numberOfOutputs: 0 });
      if (aecLoopback) { try { loop = await loopbackOut(ctx); } catch (e) { console.warn('loopback unavailable', e?.message); loop = null; } }
      player = createPlayer(ctx, loop ? loop.node : ctx.destination);
    } else {
      player = createSfuPlayer();
    }
    ws = new WebSocket(`${base}/call?${q}`, ['hb.v1', `hbtoken.${token}`]);
    ws.binaryType = 'arraybuffer';
    ws.onmessage = (ev) => { onMessage(ev).catch((e) => console.warn('voice message', e)); };
    ws.onclose = (ev) => drop(ev.reason === 'hangup' ? '' : 'The voice agent hung up.', ev.code === 4402 ? 'minutes' : (ev.reason || 'closed'));
    ws.onerror = () => {};
    await new Promise((resolve, reject) => { ws.onopen = resolve; ws.addEventListener('close', () => reject(new Error('The voice agent refused the call.')), { once: true }); });
    if (transport === 'sfu') await sfuOffer();
    else startMic(ctx, ctx.createMediaStreamSource(mic));
  } catch (e) {
    fail(e);
  }
  return started;
}
